import { Link, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';

import JaberAlkhawaterLogo from '../assets/jaber-alkhawater-logo.png';
import { cdnImage } from '@/lib/cdn';
import { Menu } from 'lucide-react';

const Navbar = () => {
	let appContext = { isLogged: false, user: ' ', email: ' ', role: 'USER' };
	appContext = JSON.parse(localStorage.getItem('appContext')!);

	const location = useLocation();
	const menuItems = [
		{ id: 1, path: '/', label: 'الرئيسية', beLogin: false, role: 'USER' },
		{ id: 2, path: '/lessons', label: 'ملخصات', beLogin: false, role: 'USER' },
		{ id: 3, path: '/lessons-admin', label: 'إدارة الدروس', beLogin: true, role: 'ADMIN' },
		{ id: 4, path: '/clients-admin', label: 'إدارة الزبائن و الإشتراكات', beLogin: true, role: 'ADMIN' },
		{ id: 6, path: '/messages-admin', label: 'الرسائل', beLogin: true, role: 'ADMIN' },
		//{ id: 5, path: '/profile', label: 'الملف الشخصي', beLogin: true, role: 'USER' },
	];
	const isActive = (path: string) => location.pathname === path;

	const doLogout = async () => {
		// Clearing localStorage only changes what this page draws; the session cookie
		// is what the API trusts, so it has to be dropped server-side too.
		try {
			await fetch('/api/logout', { method: 'POST' });
		} catch (error) {
			console.error('Failed to log out:', error);
		}

		localStorage.setItem('isSetAppContext', 'TRUE');
		const appContext = { isLogged: false, user: ' ', email: ' ', role: 'USER' };
		localStorage.setItem('appContext', JSON.stringify(appContext));
		window.location.replace('/');
	};

	// Edge-to-edge green bar on phones; the rounded card look starts at sm.
	return (
		<nav className="rounded-none sm:rounded-[8px]" style={{ backgroundColor: 'rgb(25 135 84)' }}>
			<div className="container mx-auto flex items-center justify-between py-4">
				<Link to="/" className="flex items-center gap-3">
					<div className="flex h-10 w-10 items-center justify-center">
						<img src={cdnImage('jaber-alkhawater-logo.png', JaberAlkhawaterLogo)} alt="جبر الخواطر" className="text-primary-foreground" />
					</div>
					<span className="text-xl font-bold" style={{ color: '#ffc107' }}>
						جبر الخواطر
					</span>
				</Link>

				{/* Desktop Links */}
				<div className="hidden items-center gap-8 lg:flex">
					{menuItems.map((item) => {
						if (item.role === 'USER') {
							return (
								<Link
									key={item.id}
									to={item.path}
									className={`text-sm font-medium transition-colors hover:text-primary ${
										isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
									}`}
									style={{ color: '#ffc107', fontSize: '16px' }}>
									{item.label}
								</Link>
							);
						} else {
							if (item.role === appContext.role) {
								return (
									<Link
										key={item.id}
										to={item.path}
										className={`text-sm font-medium transition-colors hover:text-primary ${
											isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
										}`}
										style={{ color: '#ffc107', fontSize: '16px' }}>
										{item.label}
									</Link>
								);
							}
						}
					})}
					{appContext.isLogged && (
						<Link
							key={5}
							to="/profile"
							className={`text-sm font-medium transition-colors hover:text-primary ${
								isActive('/profile') ? 'text-primary' : 'text-muted-foreground'
							}`}
							style={{ color: '#ffc107', fontSize: '16px' }}>
							{'الملف الشخصي'}
						</Link>
					)}
				</div>

				{/* Auth Buttons */}
				<div className="hidden items-center gap-3 lg:flex">
					<Link to="/contact">
						<Button variant="ghost" size="sm" style={{ color: 'white' }}>
							تواصل معنا
						</Button>
					</Link>

					{!appContext.isLogged && (
						<Link to="/login">
							<Button variant="ghost" size="sm" style={{ color: 'white' }}>
								تسجيل الدخول
							</Button>
						</Link>
					)}

					{appContext.isLogged && (
						<Button variant="ghost" size="sm" style={{ color: 'white' }} onClick={() => doLogout()}>
							تسجيل الخروج
						</Button>
					)}

					{!appContext.isLogged && (
						<Link to="/register">
							<Button size="sm">إنشاء حساب</Button>
						</Link>
					)}
				</div>

				{appContext.isLogged && (
					<div className="hidden items-center gap-3 lg:flex" style={{ color: 'white' }}>
						{appContext.user}
					</div>
				)}

				<div className="flex flex-wrap gap-2 lg:hidden">
					<Drawer direction="right">
						<DrawerTrigger asChild>
							<Button
								className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-10 w-10"
								aria-label="القائمة"
								style={{ color: '#ffc107', fontSize: '16px' }}>
								<Menu className="h-6 w-6"></Menu>
							</Button>
						</DrawerTrigger>
						<DrawerContent className="h-full" style={{ width: 'min(400px, 85vw)', backgroundColor: '#e6e6b8' }} dir="rtl">
							<DrawerHeader dir="rtl">
								<DrawerTitle dir="rtl"></DrawerTitle>
								<DrawerDescription dir="rtl"></DrawerDescription>
							</DrawerHeader>
							<div className="flex flex-col gap-6 p-4" dir="rtl" style={{ backgroundColor: '#e6e6b8' }}>
								{menuItems.map((item) => {
									if (item.role === 'USER') {
										return (
											<Link
												key={item.id}
												to={item.path}
												className={`text-sm font-bold px-3 transition-colors hover:text-primary ${
													isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
												}`}
												style={{ color: 'rgb(25 135 84)', fontSize: '16px' }}>
												{item.label}
											</Link>
										);
									} else {
										if (item.role === appContext.role) {
											return (
												<Link
													key={item.id}
													to={item.path}
													className={`text-sm font-bold px-3 transition-colors hover:text-primary ${
														isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
													}`}
													style={{ color: 'rgb(25 135 84)', fontSize: '16px' }}>
													{item.label}
												</Link>
											);
										}
									}
								})}

								{appContext.isLogged && (
									<Link
										key={5}
										to="/profile"
										className={`text-sm font-bold px-3 transition-colors hover:text-primary ${
											isActive('/profile') ? 'text-primary' : 'text-muted-foreground'
										}`}
										style={{ color: 'rgb(25 135 84)', fontSize: '16px' }}>
										{'الملف الشخصي'}
									</Link>
								)}

								<Link
									key={55}
									to="/contact"
									className="text-sm font-bold px-3 transition-colors hover:text-primary "
									style={{ color: 'rgb(25 135 84)', fontSize: '16px' }}>
									{'تواصل معنا'}
								</Link>

								{!appContext.isLogged && (
									<Link to="/login">
										<Button variant="ghost" size="sm" style={{ color: 'rgb(25 135 84)' }}>
											تسجيل الدخول
										</Button>
									</Link>
								)}

								{!appContext.isLogged && (
									<Link to="/register">
										{/* The default variant's foreground is near-invisible on its own
										    dark-green fill in this theme; force plain white. */}
										<Button size="sm" style={{ color: 'white' }}>
											إنشاء حساب
										</Button>
									</Link>
								)}

								{appContext.isLogged && (
									<div className="hidden items-center gap-3 lg:flex" style={{ color: 'rgb(25 135 84)' }}>
										{appContext.user}
									</div>
								)}

								{appContext.isLogged && (
									<Button variant="ghost" size="sm" style={{ color: 'rgb(25 135 84)' }} onClick={() => doLogout()}>
										تسجيل الخروج
									</Button>
								)}
							</div>
							<DrawerFooter dir="rtl"></DrawerFooter>
						</DrawerContent>
					</Drawer>
				</div>
			</div>
		</nav>
	);
};

export default Navbar;
